import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useMemo } from 'react';

import { CheckboxDumb } from '@webapp/common/components/checkbox-dumb';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import type { SmileName } from '@webapp/common/components/smile-image';
import { SmileImage, SmilePackName } from '@webapp/common/components/smile-image';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { CDN_URL } from '@webapp/common/conf';
import { setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import { PLACEHOLDER } from '@webapp/common/resources/fillers';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';

import { SvgInline } from './svg-inline';

import css from './smile.css';

const getExt = (filename: string) => filename?.split('.')?.pop() || false;

export const Smile: VFC<SurveyQuestion> = ({
    question: {
        id: questionId,
        answersActors,
        responseActor,
        params: { size, smilePack, cantRate, cantRateText },
    },
    actor,
}) => {
    const count = answersActors.length;
    const response = useSelector(responseActor, (r) => r.context);
    const { textStyle } = useTextStyle();
    const style = useMemo(
        () => ({
            gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
        }),
        [count],
    );

    const cantRateChecked = response?.value === CANTRATE_ID;

    const handleChecked = useCallback(
        (checked) => {
            responseActor.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : '', questionId });
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', questionId, value: checked ? CANTRATE_ID : '' });
        },
        [responseActor, actor, questionId],
    );

    const handleSelect = useCallback(
        (value: number) => () => {
            responseActor.send({ type: 'CHANGE', value, questionId });
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', questionId, value, answerId: value });
        },
        [responseActor, actor, questionId],
    );

    const hasSelected = answersActors.some((answerActor) => {
        const answer = answerActor.getSnapshot().context;

        return Boolean(answer.response.getSnapshot().context.value);
    });

    return (
        <div className={css.container}>
            <div className={cn(CssQuestionType.QUESTION, CssQuestionType.SMILES, css.smiles)} style={style}>
                {answersActors.map((answerActor) => {
                    const answer = answerActor.getSnapshot().context;
                    const selected = Boolean(answer.response.getSnapshot().context.value);
                    const label = answer.value[1] || '';
                    const name = answer.value[0] as SmileName;
                    const onChange = (): void => void handleSelect(answer.id)();

                    const render = () => {
                        const isSvg = getExt(answer?.file?.path) === 'svg';
                        const path = `${CDN_URL + answer?.file?.path}`;

                        if (smilePack === SmilePackName.PACK4) {
                            const wSize = size === 1 ? 50 : 78;

                            return (
                                <div
                                    className={css.customImage}
                                    style={{
                                        width: wSize,
                                        height: wSize,
                                        opacity: hasSelected && !selected ? '0.6' : '1',
                                    }}
                                >
                                    {!isSvg ? (
                                        <img
                                            height={wSize}
                                            loading='lazy'
                                            src={path}
                                            width={wSize}
                                            onClick={onChange}
                                        />
                                    ) : (
                                        <SvgInline src={path} size={size} onClick={onChange} />
                                    )}
                                </div>
                            );
                        }

                        return <SmileImage name={name} pack={smilePack} size={size} onClick={onChange} />;
                    };

                    return (
                        <div
                            key={answer.id}
                            className={cn(CssPageBlock.ANSWER, css.smile, {
                                [css.selected]: selected,
                                smileSelected: selected,
                                [css.notSelected]: hasSelected ? !selected : false,
                            })}
                        >
                            {render()}
                            {label && (
                                <div
                                    className={cn(CssUiComponent.LABEL, css.smileText)}
                                    style={textStyle}
                                    {...setHtml(label)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            {cantRate && (
                <div>
                    <CheckboxDumb
                        checked={cantRateChecked}
                        className={css.cantRateCheckbox}
                        label={cantRateText || PLACEHOLDER.CANT_RATE}
                        round={false}
                        onChange={handleChecked}
                    />
                </div>
            )}
        </div>
    );
};
Smile.displayName = 'Smile';
